// Phase 24 — Primary-CTA-Text-Switch (EUA-03 / D-04). Submit-Logik unverändert.
import { Alert, Button, Card, Col, Row, Space, Tooltip } from "antd"
import { useApolloClient, useMutation } from "@apollo/client"
import React, { FC, memo, useEffect, useMemo, useRef, useState } from "react"
import { useHistory, useLocation, useParams } from "react-router-dom"
import {
    Attendee,
    Booker,
    Course,
    GetCourseDetailsByIdPublicDocument,
    GetCourseDetailsByIdPublicQuery,
    GetCourseDetailsByIdPublicQueryVariables,
    GetUpdatedBookerQuery,
    NewBookingInput,
    useInitiateMolliePaymentForBookingFromEndUserAppMutation,
    useGetUpdatedBookerQuery,
    useGetAvailablePaymentMethodsFromEndUserAppQuery,
    useGetBankTransferProviderQuery,
    GetUpdatedBookerDocument,
    PaymentInterval,
    SettingEndUserAppQuery,
    useSettingEndUserAppQuery,
    SettingEndUserApp,
    OptionalInputFieldsInput,
    BookingRestrictionErrors,
    PaymentProvider,
    useGetCompanyCurrencyPublicQuery,
} from "../../../generated/graphql"
import { clampTimeoutDelay } from "../../../helpers/clampTimeoutDelay"
import { getPaymentMethodType } from "../../../helpers/getPaymentMethodType"
import { embedReturnBaseUrl } from "../../../helpers/embedMode"
import { isStandardBankTransferViaMollie, MOLLIE_BANKTRANSFER_METHOD } from "../helpers/isStandardBankTransferViaMollie"
import { gateMollieMethodsForDevice } from "../helpers/gateMollieMethodsForDevice"
import { margin } from "../../../styles/layout"
import { useMediaSize } from "../../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import SummaryRight from "../Steps/StepSummary/SummaryRight"
import SummaryLeft from "../Steps/StepSummary/SummaryLeft"
import { URL_BASE, URL_COURSE_DETAILS, URL_LOGIN, URL_PAYMENT_RETURN, URL_RESULTS } from "../../../models/url"
import css from "./BookingSummary.module.css"
import { blue, red } from "@ant-design/colors"
import RequiredAlert from "./RequiredAlert"
import { RestrictionsType, getBookingRestrictions } from "../../common/helpers"
import { attendeeCheck, effectiveBookingRestriction, isAttendeeSelectableForCourse } from "../../../views/SelectAttendeeTable/checkAttendee"
import PrerequisiteCourses from "../../CourseDetails/PrerequisiteCourses"
import { getDisabledEntries, isSubmitButtonDisabled, StatusData } from "../Steps/StepSummary/disabledText"
import { isPaymentMethodUsableForBooking } from "../../../helpers/isPaymentMethodUsableForBooking"
import { CheckoutBlockerCode, reportEmbedConversion, reportEndUserEvent } from "../../../services/reportEndUserEvent"
import { bookingConversionValue, parentReportConversion } from "../../../helpers/parentIFrame"
import { useTranslation } from "react-i18next"
import * as Sentry from "@sentry/react"
import { getGraphQLErrorCode, getGraphQLErrorMessage } from "../../../helpers/sentry"
import { safeStorage } from "../../../helpers/safeStorage"
import { discountCodeRejectText, getDiscountCodeRejection, rememberAppliedDiscount } from "./BookingPricePreview/discountCodeMessages"
import { keepCourseAdditionalItemIds } from "./BookingPricePreview/additionalItemSelection"
import { resolvePaymentTypeChoice } from "./BookingPricePreview/paymentTypeChoice"
import { safeJsonParse } from "../../../helpers/safeJsonParse"
import { mapLocaleToMollie } from "../hooks/mapLocaleToMollie"
import { CREATE_BOOKING_WITH_SCHEDULED_PAYMENTS } from "../bookings.graphql"
import { getMolliePendingKey, MolliePendingPaymentEntry, removeStaleMolliePendingEntries } from "../hooks/useMolliePendingPayment"
import { getLateEntryGate, QuoteState, QUOTE_ERROR } from "./lateEntryQuote"
import { readUtmSource } from "./utmSource"
import { isBillingAddressIncomplete, withBillingAddressIfEnabled } from "../Steps/StepSummary/billingAddress"

export type ModeType = "PlaceReservation" | "createBooking"
export type NewBookingInputKeys = keyof NewBookingInput

type BookingSummaryProps = {}

type CreateBookingWithScheduledPaymentsResult = {
    createBooking: {
        id: string
        bookingType: string
        scheduledPayments?: Array<{
            id: string
            paymentDone?: boolean | null
            paymentAmount?: number | null
        } | null> | null
    }
}

type CreateBookingWithScheduledPaymentsVariables = {
    newBookingData: NewBookingInput
    sendMail: boolean
    onlineBooking: boolean
}

function getFirstUnpaidScheduledPayment(
    scheduledPayments: CreateBookingWithScheduledPaymentsResult["createBooking"]["scheduledPayments"],
): NonNullable<NonNullable<CreateBookingWithScheduledPaymentsResult["createBooking"]["scheduledPayments"]>[number]> | null {
    const firstUnpaid = (scheduledPayments || []).find((scheduledPayment) => {
        return scheduledPayment && scheduledPayment.paymentDone !== true
    })
    return firstUnpaid ?? null
}

function writeMolliePendingEntry(bookingId: string, entry: MolliePendingPaymentEntry): void {
    removeStaleMolliePendingEntries()
    try {
        window.localStorage.setItem(getMolliePendingKey(bookingId), JSON.stringify(entry))
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("[BookingSummary] Mollie pending entry konnte nicht gespeichert werden", error)
    }
}

const BookingSummary: FC<BookingSummaryProps> = () => {
    const mediaSize = useMediaSize()
    const { media } = mediaSize
    const { t, i18n } = useTranslation()
    const mediaColumns = media === "xxl" ? 4 : media === "xl" ? 3 : media === "md" ? 2 : media === "sm" ? 2 : 1
    const isLoggedIn = !!safeStorage.getItem("loggedInUser")
    const history = useHistory()
    const location = useLocation()
    const { courseId: courseIdFromUrl } = useParams<{ courseId?: string }>()
    const [course, setCourse] = useState<Course>()
    const [booker, setBooker] = useState<Booker>(safeJsonParse<Booker>(safeStorage.getItem("booker")) as Booker)
    const [attendeeId, setAttendeeId] = useState<string>()
    const [attendee, setAttendee] = useState<Attendee>()
    const [mode, setMode] = useState<ModeType>("createBooking")
    const [bookingData, setBookingData] = useState<NewBookingInput>(safeJsonParse<NewBookingInput>(safeStorage.getItem("bookingData")) as NewBookingInput)
    const [attendeeAgeDoesFitCourse, setAttendeeAgeDoesFitCourse] = useState(false)
    const [noAttendeeDoesFitCourse, setNoAttendeeDoesFitCourse] = useState(false)
    const [allAttendeesAlreadyBooked, setAllAttendeesAlreadyBooked] = useState(false)
    const [validSepaDebitDataValidIfSelected, setValidSepaDebitDataValidIfSelected] = useState(true)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [creatingAttendee, setCreatingAttendee] = useState(false)
    const [showAlert, setShowAlert] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    // Synchroner Doppelklick-Schutz UND Submit-Freeze-Flag: isSubmitting ist React-State
    // und greift erst nach dem Re-Render. Der Ref ist synchron und wird ausserdem genutzt,
    // um waehrend einer laufenden Buchung eingehende Booker-Refetches zu ignorieren (F-001).
    const submittingRef = useRef(false)
    // Kundenakte (Checkout-Sackgassen): wurde createBooking in dieser Sitzung gestartet?
    // Nur dann ist ein Verlassen der Seite KEIN Abbruch.
    const bookingStartedRef = useRef(false)
    // Zustand des letzten Renders für die Abbruch-Meldung im Cleanup (keine veralteten Closures).
    const latestReportRef = useRef<{
        ready: boolean
        courseId: string | null
        attendeeId: string | null
        blockerCodes: Array<CheckoutBlockerCode>
        hasPaymentMethod: boolean
    }>({ ready: false, courseId: null, attendeeId: null, blockerCodes: [], hasPaymentMethod: false })
    const [inputFieldValues, setInputFieldValues] = useState<Array<{ [Key: string]: string }>>([])
    const [selectedMollieMethod, setSelectedMollieMethod] = useState<string | null>(null)
    const [previewOpenAmount, setPreviewOpenAmount] = useState<number | null>(null)
    // Late-Entry-Pricing-Vertrag (Phase 3):
    // - quote === undefined: Preview nicht angebunden (kein Late-Entry-Flow) → kein Gate.
    // - quote === null: neuer Preview-Request läuft → Submit gesperrt.
    // - quote-Objekt: fertige Server-Preview; Fingerprint/Version wandern atomar in den Submit.
    // Bewusst NUR in-memory (kein localStorage), damit kein Stale-Fingerprint aus einer
    // alten Session in einen Submit gelangt.
    const [quote, setQuote] = useState<QuoteState>()
    // Erzwingt einen frischen Preview-Request (validUntil-Ablauf / PRICE_QUOTE_CHANGED / Retry).
    const [refreshNonce, setRefreshNonce] = useState(0)
    // Hinweis "Der Preis wurde aktualisiert" nach automatischem validUntil-Refresh.
    const [priceUpdatedHint, setPriceUpdatedHint] = useState(false)
    // Hinweis + Sperre nach PRICE_QUOTE_CHANGED aus dem Submit (Neubestätigung nötig).
    const [priceChangedNotice, setPriceChangedNotice] = useState(false)
    // Der beim PRICE_QUOTE_CHANGED abgelehnte Fingerprint: Submit bleibt gesperrt, bis die
    // Preview einen NEUEN (≠ abgelehnten) Fingerprint liefert (verhindert Re-Submit derselben
    // stale Quote → Schleife, F2/F4).
    const [rejectedFingerprint, setRejectedFingerprint] = useState<string | null>(null)
    // B-82808: Vom Server mit PAYMENT_METHOD_INCOMPLETE abgelehnte Methode (ID + Lastschrift-Daten
    // zum Ablehnungszeitpunkt). Submit bleibt gesperrt, bis der Kunde eine andere Methode wählt
    // oder Kontoinhaber/IBAN der abgelehnten nachträgt.
    const [rejectedPaymentMethodKey, setRejectedPaymentMethodKey] = useState<string | null>(null)
    // Teilnehmer, den der Server wegen der Altersgrenze abgelehnt hat (mit Geburtsdaten zum Zeitpunkt der Ablehnung).
    // Getrennt vom Ergebnis der Browser-Prüfung, weil die Kindkomponenten ihr (älteres) Ergebnis bei jedem Render
    // zurückschreiben. Ein anderer Teilnehmer oder ein korrigiertes Geburtsdatum heben die Sperre auf.
    const [ageRejectionKey, setAgeRejectionKey] = useState<string | null>(null)
    const currentAgeKey = [
        bookingData?.attendeeId,
        booker?.attendees?.find((attendee) => attendee.id === bookingData?.attendeeId)?.birthday,
        booker?.birthday,
    ].join("|")
    // Hinweis nach DISCOUNT_CODE_INVALID aus dem Submit: Code wurde entfernt, Preis neu berechnet.
    const [discountCodeRejectedNotice, setDiscountCodeRejectedNotice] = useState<string | null>(null)
    const discountCodeRejectedNoticeRef = useRef<HTMLDivElement>(null)

    // Der Hinweis steht unten über dem unteren Button. Wer oben abgeschickt hat, sähe ihn sonst nicht
    // und würde mit dem nächsten Klick ungewollt zum normalen Preis buchen.
    useEffect(() => {
        if (discountCodeRejectedNotice) {
            discountCodeRejectedNoticeRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" })
        }
    }, [discountCodeRejectedNotice])
    // Löst der Kunde nach der Ablehnung einen neuen Code ein, ist der Hinweis auf den regulären Preis überholt.
    useEffect(() => {
        if (bookingData?.discountCode) setDiscountCodeRejectedNotice(null)
    }, [bookingData?.discountCode])
    // Titel gewählter Zusatzposten, die der neu geladene Kurs nicht mehr anbietet (B-842: nichts still entfernen).
    const [droppedAdditionalItemTitles, setDroppedAdditionalItemTitles] = useState<Array<string>>([])
    const droppedAdditionalItemsNoticeRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        if (droppedAdditionalItemTitles.length > 0) {
            droppedAdditionalItemsNoticeRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" })
        }
    }, [droppedAdditionalItemTitles])
    // Rate-Limit NUR für automatische (Timer-)Refreshes: verhindert eine Endlosschleife,
    // falls eine frische Quote sofort wieder abgelaufen zurückkommt.
    // ponytail: fester 15s-Floor; feinere Steuerung erst, wenn Telemetrie sie fordert.
    const lastAutoRefreshRef = useRef(0)

    // Automatischer (gedrosselter) Refresh — nur für den validUntil-Timer.
    const triggerPreviewRefresh = () => {
        const now = Date.now()
        if (now - lastAutoRefreshRef.current < 15000) return
        lastAutoRefreshRef.current = now
        setRefreshNonce((nonce) => nonce + 1)
    }

    // User-getriggerter Refresh (Retry-Button, PRICE_QUOTE_CHANGED) — UNGEDROSSELT, sonst
    // wäre er nach einem kurz zuvor gelaufenen Auto-Refresh ein No-op (F2). Setzt den
    // Throttle-Zeitstempel mit, damit der Auto-Timer nicht direkt hinterherfeuert.
    const forcePreviewRefresh = () => {
        lastAutoRefreshRef.current = Date.now()
        setRefreshNonce((nonce) => nonce + 1)
    }
    // Mollie-Methode wählen — paymentMethodId bleibt intakt (Backend braucht sie für E-Mail/Buchung)
    // Die gegenseitige Exklusivität ist nur visuell (PaymentMethodTable) und im Payment-Flow (submitBooking)
    const handleMollieMethodSelect = (methodId: string | null) => {
        setSelectedMollieMethod(methodId)
    }
    const bookerHasAttendees = Boolean(booker?.attendees?.length && booker?.attendees?.length > 0)
    const selfBookerCourse = course?.courseType.selfBookerCourse

    // Move selectedAttendeeId state here
    const [selectedAttendeeId, setSelectedAttendeeId] = useState<string>()

    // Update attendeeId whenever selectedAttendeeId changes
    useEffect(() => {
        if (selectedAttendeeId && bookingData?.attendeeId !== selectedAttendeeId) {
            setAttendeeId(selectedAttendeeId)
            updateBookingData("attendeeId", selectedAttendeeId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingData?.attendeeId, selectedAttendeeId])

    // HANDLE MEMBER PARTICIPATION REQUIREMENTS ------------------------------------------------------------------

    const restrictions: RestrictionsType = getBookingRestrictions(course?.bookingRestriction)
    const bookingRestriction = useMemo(() => effectiveBookingRestriction(course), [course])
    const { membersOnly } = restrictions
    const isCourseWithPreRequisites = Boolean(restrictions.prerequisiteCourses && restrictions.prerequisiteCourses.length > 0)

    // Selbstbucher-Kurse haben KEINE Teilnehmer-Auswahl: Der Teilnehmer wird allein über
    // `attendee.customer.id === booker.customer.id` aufgelöst. Greift das nicht, legte die
    // App stillschweigend einen NEUEN Teilnehmer an — der ist zwangsläufig in keinem
    // Voraussetzungskurs gebucht, weshalb der Server die Buchung erst beim Absenden ablehnt
    // ("kann auswählen, aber nicht bezahlen"; Realfall msv01 08/2026). Beim nächsten Versuch
    // wird dann genau diese Dublette gewählt und erneut abgelehnt — deshalb reicht es NICHT,
    // nur auf die Existenz des eigenen Datensatzes zu prüfen; er muss auch buchbar sein.
    // In beiden Fällen wählt der Kunde stattdessen aus seinen vorhandenen Teilnehmern aus.
    const bookerCustomerId = booker?.customer?.id ?? booker?.customerId
    const bookersOwnAttendee = bookerCustomerId ? booker?.attendees?.find((attendee) => attendee.customer?.id === bookerCustomerId) : undefined
    const bookersOwnAttendeeSelectable =
        bookersOwnAttendee !== undefined &&
        isAttendeeSelectableForCourse({
            attendee: bookersOwnAttendee,
            courseId: course?.id,
            isCourseWithPreRequisites,
            bookingRestriction,
            isMemberOnlyCourse: membersOnly,
        })
    const selfBookerAttendeeChoiceRequired = Boolean(selfBookerCourse && bookerHasAttendees && !bookersOwnAttendeeSelectable)

    // Voraussetzung fehlt, aber die Beschränkung erlaubt die Warteliste: Der Server legt einen
    // Wartelisteneintrag an, auch bei freien Plätzen. Der Checkout läuft deshalb im Wartelisten-Modus.
    const bookingAttendee = booker?.attendees?.find((candidate) => candidate.id === bookingData?.attendeeId)
    const prerequisiteWaitlistOnly = Boolean(
        bookingAttendee && course?.id && attendeeCheck(bookingAttendee, course.id, isCourseWithPreRequisites, bookingRestriction).waitlistOnly
    )
    // Der erzwungene Wartelisteneintrag zählt gegen maxWaitlist (null = unbegrenzt), auch wenn der Kurs freie Plätze hat.
    const prerequisiteWaitlistFull = prerequisiteWaitlistOnly && course?.maxWaitlist != null && course.waitListCount >= course.maxWaitlist

    // HANDLE PARTICIPATION REQUIREMENTS -------------------------------------------------------------------------
    const [checkedOptions, setCheckedOptions] = useState<{ [id: string]: Array<string> }>({})
    const [requermentGroupsMet, setRequermentGroupsMet] = useState<{ [id: string]: boolean }>({})
    const allReirementGroupsMet = Object.values(requermentGroupsMet).every((value) => value === true)
    // HANDLE PARTICIPATION REQUIREMENTS -------------------------------------------------------------------------

    // GETTING APP SETTINGS --------------------------------------------------------------------------------------
    const [appSettings, setAppSettings] = useState<SettingEndUserAppQuery["settingEndUserAppPublic"]>()
    const billingAddressEnabled = appSettings?.billingAddressInCheckoutEnabled === true

    const { data: appSettingsData, loading: loadingAppSettings, error: errorAppSettings } = useSettingEndUserAppQuery()

    useEffect(() => {
        if (appSettingsData) {
            setAppSettings(appSettingsData.settingEndUserAppPublic)
        }
    }, [appSettingsData, loadingAppSettings, errorAppSettings])

    // WS-1f — Tenant-Provider für Überweisungen (Manual | Mollie), aus den
    // Org-Settings. `bankTransferProvider === Mollie` bedeutet: eine im Standard-
    // Flow gewählte „Überweisung" wird server-seitig zu einer Mollie-banktransfer-
    // Zahlung → wir lösen sie sofort aus (siehe submitBooking → effectiveMollieMethod).
    const { data: bankTransferProviderData } = useGetBankTransferProviderQuery({
        fetchPolicy: "cache-first",
    })
    const bankTransferProvider: PaymentProvider | null =
        bankTransferProviderData?.settings?.bankTransferProvider ?? null

    // Phase 24 / D-03 — Mollie-Methodenliste als Proxy fuer Subscription.featureMollie
    // (Backend `getAvailablePaymentMethodsFromEndUserApp` gated bereits server-seitig auf
    // featureMollie + Mollie-Connection — leere Liste => kein Mollie verfuegbar).
    //
    // WICHTIG (Cache-Durchschlag-Fix): Die Query MUSS mit `courseId` aufgerufen werden.
    // Ohne `courseId` trifft sie den Rueckwaerts-Kompatibilitaets-Pfad des Resolvers
    // (kein Kurs-Filter) und liefert IMMER die volle Methodenliste — selbst fuer Kurse
    // mit `allowMolliePayment=false`. In diesem Fall haengt der Mollie-Modus faktisch nur
    // noch an `course?.allowMolliePayment`, das ueber den 300ms-Storage-Sync (s.u.) bei
    // SPA-Navigation zwischen Buchungen kurzzeitig "stale" sein kann — wodurch der
    // Mollie-Block faelschlich bei einem Nicht-Mollie-Kurs erscheint. Mit `courseId`
    // liefert das Backend fuer Nicht-Mollie-Kurse eine leere Liste, sodass
    // `mollieMethodsCount === 0` den Mollie-Modus zuverlaessig verhindert.
    const { data: mollieMethodsData } = useGetAvailablePaymentMethodsFromEndUserAppQuery({
        variables: { courseId: course?.id },
        skip: !course?.id,
        fetchPolicy: "cache-first",
    })
    // Geraete-Gating konsistent zu SummaryRight/MollieMethodsBlock: nur wirklich nutzbare
    // Methoden zaehlen (Apple Pay nur auf Apple-Geraeten). Sonst bliebe der Mandatory-Mode
    // und damit der Submit-Button bei einem Wallet-only-Kurs auf Nicht-Apple-Geraeten
    // haengen, obwohl SummaryRight korrekt auf die klassische Tabelle zurueckfaellt.
    const mollieMethodsCount = gateMollieMethodsForDevice(
        mollieMethodsData?.getAvailablePaymentMethodsFromEndUserApp,
    ).length
    // Phase 24 / D-04 — Boolean-strict (=== true): null/undefined/false/0 fallen auf klassischen Pfad.
    //
    // Phase 4 — Drei Kurs-Zustaende im Checkout:
    // - Pflicht (mandatory): `allowMolliePayment===true && mollieOptional!==true` —
    //   Online ist die EINZIGE Zahlart, MollieMethodsBlock, Submit erst nach Methodenwahl.
    // - Kombi (combined): `allowMolliePayment===true && mollieOptional===true` — Online
    //   ADDITIV zu den eigenen Methoden in EINER flachen Tabelle; der Nutzer waehlt
    //   entweder eine eigene Methode (paymentMethodId) ODER eine Mollie-Methode.
    // - Klassik: alles andere — eigene Methoden, unveraendert (EUA-08 Byte-Identity).
    // `!== true` behandelt `null`/`undefined` als Pflicht (rueckwaertskompatibel).
    const isMollieMandatoryMode = course?.allowMolliePayment === true && course?.mollieOptional !== true && mollieMethodsCount > 0
    const isMollieCombinedMode = course?.allowMolliePayment === true && course?.mollieOptional === true && mollieMethodsCount > 0

    // GET OPTIONAL INPUT FIELDS ---------------------------------------------------------------------------------
    const [optionalInputFields, setOptionalInputFields] = useState<SettingEndUserApp["optionalInputFields"]>([])

    useEffect(() => {
        const updatedFields: Array<OptionalInputFieldsInput> = []
        appSettings?.optionalInputFields?.forEach((field: OptionalInputFieldsInput) => {
            if (field.enabeld) {
                updatedFields.push(field)
            }
        })
        // Einmal nach der Schleife setzen — vorher pro Iteration aufgerufen, was
        // bei N Feldern N State-Updates triggert (React 18 batched es, aber semantisch falsch).
        setOptionalInputFields(updatedFields)
    }, [appSettings])

    // -----------------------------------------------------------------------------------------------------------

    const [createBooking] = useMutation<
        CreateBookingWithScheduledPaymentsResult,
        CreateBookingWithScheduledPaymentsVariables
    >(CREATE_BOOKING_WITH_SCHEDULED_PAYMENTS, {
        refetchQueries: ["GetUpdatedBooker", { query: GetUpdatedBookerDocument, variables: { id: booker?.id ?? "" } }],
    })

    const [initiateMolliePayment] = useInitiateMolliePaymentForBookingFromEndUserAppMutation()

    // Nur für den Conversion-Wert (sc-3366): BookingPricePreview lädt die Währung
    // ohnehin, hier reicht der Cache. Fehlt sie, wird die Conversion ohne Wert gemeldet.
    const { data: companyCurrencyData } = useGetCompanyCurrencyPublicQuery({ fetchPolicy: "cache-only" })

    const { data: getBookerData, loading: loadingBooker, error: getBookerError, refetch: refetchBooker } = useGetUpdatedBookerQuery({ variables: { id: booker?.id ?? "" } })

    useEffect(() => {
        // F-001: Waehrend eine Buchung laeuft KEINE neuen Booker-Daten uebernehmen.
        // Die createBooking-Mutation refetcht GetUpdatedBooker; der frische Booker enthaelt
        // den soeben gebuchten Teilnehmer als "bereits gebucht", was — noch VOR der (bei Mollie
        // asynchronen) Navigation — Teilnehmer-Warnung, Fehlerliste und Submit-Disable
        // aufblitzen laesst. Wir buchen ohnehin weg; das Einfrieren des Booker-State an dieser
        // schmalsten Stelle unterbindet die gesamte Fehler-Neuberechnung an der Quelle.
        if (getBookerData && !submittingRef.current) {
            const fetchedBookerData: GetUpdatedBookerQuery["booker"] | undefined = getBookerData?.booker
            const fetchedBookerAsBooker: Booker = fetchedBookerData as Booker
            setBooker(fetchedBookerAsBooker)
            safeStorage.setItem("booker", JSON.stringify(fetchedBookerAsBooker))
        }
    }, [getBookerData, getBookerError, loadingBooker])

    useEffect(() => {
        if (bookingData?.attendeeId) setAttendeeId(bookingData.attendeeId)
    }, [bookingData])

    // B-82808: Schlüssel der gewählten eigenen Methode (ID + Lastschrift-Daten). Ändert sich bei
    // anderer Auswahl ODER wenn Kontoinhaber/IBAN nachgetragen werden — beides hebt die Sperre
    // nach PAYMENT_METHOD_INCOMPLETE auf.
    const selectedOwnPaymentMethod = booker?.paymentMethods?.find((paymentMethod) => paymentMethod.id === bookingData?.paymentMethodId)
    const selectedPaymentMethodKey = [bookingData?.paymentMethodId ?? "", selectedOwnPaymentMethod?.sepaAccountholder ?? "", selectedOwnPaymentMethod?.iban ?? ""].join("|")

    // validUntil-Ablauf bei offenem Checkout: Timer auf den Ablaufzeitpunkt setzen; beim
    // Feuern Submit sperren (Gate greift über quote) + Preview automatisch neu anfordern +
    // "Der Preis wurde aktualisiert"-Hinweis. Rate-Limit in triggerPreviewRefresh verhindert
    // einen Loop, falls die frische Quote sofort wieder abgelaufen zurückkommt. Der neue
    // Response bringt ein neues validUntil → dieser Effect plant dann den nächsten Timer.
    useEffect(() => {
        if (!quote || typeof quote !== "object" || !quote.validUntil) return
        const validUntilMs = Date.parse(quote.validUntil)
        if (isNaN(validUntilMs)) return
        const fire = () => {
            setPriceUpdatedHint(true)
            triggerPreviewRefresh()
        }
        const delay = validUntilMs - Date.now()
        if (delay <= 0) {
            fire()
            return
        }
        // Int32-Clamp: Ein weit entferntes validUntil (> ~24,86 Tage) würde den
        // setTimeout-Delay überlaufen lassen und je nach Datum SOFORT feuern —
        // Hint + Auto-Refresh ohne echten Ablauf (kippte kalenderabhängig).
        const timer = setTimeout(fire, clampTimeoutDelay(delay))
        return () => clearTimeout(timer)
        // triggerPreviewRefresh ist stabil genug (nur Refs/Setter); bewusst nur an quote gekoppelt.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quote])

    // Nach einer akzeptierten NEUEN Quote (Fingerprint ≠ abgelehnter) den PRICE_QUOTE_CHANGED-
    // Zustand aufräumen: Banner weg (F4), abgelehnten Fingerprint vergessen (F2).
    useEffect(() => {
        if (quote && typeof quote === "object" && quote.fingerprint && quote.fingerprint !== rejectedFingerprint) {
            if (rejectedFingerprint) setRejectedFingerprint(null)
            if (priceChangedNotice) setPriceChangedNotice(false)
        }
    }, [quote, rejectedFingerprint, priceChangedNotice])

    if (getBookerError) {
        const rawErrorMessage = getGraphQLErrorMessage(getBookerError) || ""
        if (rawErrorMessage.includes("Access token not valid") || rawErrorMessage.includes("Bad Request booker")) {
            history.push(URL_RESULTS.replace(":resultType", "loggedOut"))
        } else {
            Sentry.captureException(getBookerError)
            history.push(URL_RESULTS.replace(":resultType", "unknown"))
        }
    }

    // Kundenakte: "Buchen" geklickt, aber der Submit ist gesperrt. Codes statt Texte;
    // Dedupe je Kurs und Blocker-Menge liegt im Service.
    const reportCheckoutBlocked = () => {
        const latest = latestReportRef.current
        reportEndUserEvent({
            type: "CheckoutBlocked",
            step: "BookingSummary",
            courseId: latest.courseId,
            attendeeId: latest.attendeeId,
            blockerCodes: latest.blockerCodes,
            hasPaymentMethod: latest.hasPaymentMethod,
        })
    }

    const submitBooking = async () => {
        if (submittingRef.current) return
        if (disabledText.length) {
            setShowAlert(true)
            reportCheckoutBlocked()
            return
        }
        // Late-Entry-Gate: kein Submit, solange die Preview lädt/fehlt, der Kurs nicht
        // buchbar ist, validUntil abgelaufen ist oder Fingerprint/Version fehlen.
        if (lateEntryGate.blocked) {
            setShowAlert(true)
            reportCheckoutBlocked()
            if (lateEntryGate.expired) triggerPreviewRefresh()
            return
        }

        submittingRef.current = true
        setIsSubmitting(true)
        // Im catch (PRICE_QUOTE_CHANGED) benötigt: der Fingerprint, den wir gerade abgesendet
        // haben — hoisted, da im try-Block block-scoped nicht sichtbar.
        let submittedFingerprint: string | null = null
        try {
            if (bookingData) {
                // console.debug("bookingData", bookingData)

                // WS-1f — Effektive Mollie-Methode: entweder die explizit im Picker
                // gewählte (`selectedMollieMethod`) ODER — wenn der Tenant
                // Überweisungen über Mollie abwickelt (`bankTransferProvider=Mollie`)
                // und der Kunde im Standard-Flow „Überweisung" gewählt hat —
                // implizit "banktransfer". Letzteres lässt den Standard-Flow exakt
                // wie den Mollie-Picker-banktransfer laufen (sofort initiate →
                // Return-Page mit On-Screen-IBAN). Bar/Lastschrift/Nicht-Mollie-
                // Überweisung bleiben unberührt (Helper liefert dort `false`).
                const selectedStandardMethod = booker?.paymentMethods?.find(
                    (paymentMethod) => paymentMethod.id === bookingData.paymentMethodId,
                )
                const selectedStandardMethodType = selectedStandardMethod
                    ? getPaymentMethodType(selectedStandardMethod)
                    : null
                const effectiveMollieMethod = fullyCoveredByCredit
                    ? null
                    : selectedMollieMethod ||
                      (isStandardBankTransferViaMollie({
                          bankTransferProvider,
                          selectedStandardMethodType,
                          selectedMollieMethod,
                      })
                          ? MOLLIE_BANKTRANSFER_METHOD
                          : null)

                // Bei Mollie-Zahlung: keine E-Mail bei Buchungserstellung senden
                // Die Bestätigungs-E-Mail wird nach erfolgreicher Zahlung via Webhook gesendet.
                //
                // Im Wartelisten-Modus (waitlistPaymentDeferred) kennt nur der Server den
                // Ausgang: Wird zwischen Anzeige und Create ein Platz frei, entsteht statt
                // der Warteliste eine reguläre Buchung mit offener Pflichtrate (Inverse-Race,
                // siehe unten) — `sendMail=true` bestätigte sie vor der Zahlung. Mit `false`
                // entscheidet das Backend anhand des finalen `bookingType`: Warteliste
                // bekommt die Mail über `onlineBooking && !isBooking`
                // (`shouldSendConfirmationMailImmediately`), die Buchung erst nach Zahlung.
                // Guthaben-gedeckte Buchungen bleiben ausgenommen — ohne Mollie-Rate gibt es
                // keinen Webhook, der die Mail nachholt.
                const sendMail = !effectiveMollieMethod && !(waitlistPaymentDeferred && !fullyCoveredByCredit)
                // Fingerprint + Kalkulationsversion NUR im Submit-Moment aus dem Quote-State
                // ziehen (nicht via updateBookingData/localStorage) — atomarer Preis-Vertrag.
                // Fehlen sie (altes Backend / kein Late-Entry-Flow), bucht der Legacy-Pfad
                // ohne die beiden Felder weiter (Server-Fallback).
                submittedFingerprint = quote && typeof quote === "object" ? quote.fingerprint ?? null : null
                // B-82808: Ist die eigene Methode NICHT die wirksame Wahl (Online-Zahlung gewählt
                // oder Mollie-Pflicht) und zeigt `paymentMethodId` auf eine unvollständige
                // Lastschrift (z. B. leerer Teilnehmer-Standard), die ID nicht mitsenden. Der Server
                // löst dann selbst eine brauchbare Methode auf (wirft dort nie), statt die
                // Online-Buchung mit PAYMENT_METHOD_INCOMPLETE abzulehnen.
                const dropUnusableOwnMethod = !ownPaymentMethodIsEffective && selectedStandardMethod !== undefined && !isPaymentMethodUsableForBooking(selectedStandardMethod)
                const { paymentMethodId: _unusablePaymentMethodId, ...bookingDataWithoutPaymentMethod } = bookingData
                const bookingDataWithMethod: NewBookingInput = dropUnusableOwnMethod ? bookingDataWithoutPaymentMethod : bookingData
                // Ist das Rabattcode-Flag nachweislich aus, geht ein gespeicherter oder verlinkter Code nicht mit. Laden die
                // Einstellungen noch (oder schlug die Abfrage fehl), bleibt er drin: dann entscheidet der Server, statt dass
                // die Buchung still zum vollen Preis durchgeht.
                const { discountCode: _disabledDiscountCode, ...bookingDataWithoutDiscountCode } = bookingDataWithMethod
                const discountCodesDisabled = appSettings !== undefined && appSettings.discountCodesEnabled !== true
                const bookingDataWithCode: NewBookingInput = discountCodesDisabled ? bookingDataWithoutDiscountCode : bookingDataWithMethod
                // Nur Zusatzposten mitschicken, die dieser Kurs anbietet (Reste anderer Kurse, gelöschte Posten),
                // gleiche Regel wie die Preview. Ohne gesetztes Feld bleibt der Payload unverändert.
                const bookingDataToSend: NewBookingInput = bookingDataWithCode.additionalItemIds
                    ? { ...bookingDataWithCode, additionalItemIds: keepCourseAdditionalItemIds(bookingDataWithCode.additionalItemIds, course) }
                    : bookingDataWithCode
                const bookingDataWithQuote: NewBookingInput =
                    quote && typeof quote === "object" && quote.fingerprint && quote.calculationVersion
                        ? { ...bookingDataToSend, pricingFingerprint: quote.fingerprint, pricingCalculationVersion: quote.calculationVersion }
                        : bookingDataToSend
                // Herkunft aus dem Link (?utm_source=kursseite); nur mitschicken, wenn vorhanden, damit ältere Backends den Input kennen.
                const utmSource = readUtmSource()
                // Wie beim Rabattcode: nur bei nachweislich ausgeschaltetem Schalter entfernen; laden die Einstellungen
                // nicht, geht eine angehakte Adresse mit und der Server prüft sie.
                const bookingDataWithBillingAddress = withBillingAddressIfEnabled(bookingDataWithQuote, appSettings === undefined || billingAddressEnabled)
                const newBookingData: NewBookingInput = utmSource ? { ...bookingDataWithBillingAddress, utmSource } : bookingDataWithBillingAddress
                // Ab hier zählt ein Verlassen der Seite nicht mehr als Abbruch (Kundenakte).
                bookingStartedRef.current = true
                const { data: booking } = await createBooking({
                    variables: { newBookingData, sendMail, onlineBooking: true },
                })
                if (booking) {
                    const bookingId = booking.createBooking.id
                    const bookingType = booking.createBooking.bookingType

                    // Conversion an die Kundenseite (sc-3366). Wartelistenplätze zählen nicht.
                    // Im Mollie-Zweig erst, wenn feststeht, dass der Zahlungsstart die Buchung
                    // nicht als Wartelistenplatz ablehnt, aber immer vor dem Redirect.
                    let conversionReported = false
                    const reportBookingConversion = () => {
                        if (bookingType !== "Booking" || conversionReported) return
                        conversionReported = true
                        const sent = parentReportConversion({
                            event: "booking_completed",
                            transactionId: bookingId,
                            ...bookingConversionValue(booking.createBooking.scheduledPayments, companyCurrencyData?.companyCurrencyPublic?.currency),
                        })
                        reportEmbedConversion(sent)
                    }
                    const startsMolliePayment = Boolean(effectiveMollieMethod) && bookingType !== "Waitlist"
                    if (!startsMolliePayment) reportBookingConversion()

                    // F-002: Die Buchung ist angelegt — die Formular-Session ist damit
                    // verbraucht. Storage raeumen, damit ein Browser-Zurück von der
                    // Return-/Erfolgsseite NICHT auf einer halb-leeren, aber absendbaren
                    // Buchungsuebersicht landet. Der Mount-Guard leitet ohne diese Keys
                    // sauber auf die Kursdetails um. Bewusst NICHT booker/loggedInUser
                    // raeumen (User bleibt eingeloggt); die PaymentReturn-Page liest nur
                    // ihren eigenen mollie-pending-payment-Key, nicht bookingData/course.
                    safeStorage.removeItem("bookingData")
                    safeStorage.removeItem("course")
                    safeStorage.removeItem("attendeeId")

                    // Wenn Mollie-Methode (explizit oder via bankTransferProvider=Mollie):
                    // Payment initiieren und zu Mollie weiterleiten
                    if (effectiveMollieMethod && startsMolliePayment) {
                        const scheduledPayment = getFirstUnpaidScheduledPayment(booking.createBooking.scheduledPayments)
                        if (!scheduledPayment) {
                            reportBookingConversion()
                            history.push(URL_RESULTS.replace(":resultType", "paymentInitFailed"))
                            return
                        }
                        // paymentAmount fehlt (nicht abgefragt/aeltere Mocks) => Alt-Verhalten (Mollie initiieren).
                        // Nur ein ECHTES 0-€-paymentAmount (z.B. voll durch Guthaben gedeckte Rate) ueberspringt Mollie.
                        if (typeof scheduledPayment.paymentAmount === "number" && scheduledPayment.paymentAmount <= 0) {
                            reportBookingConversion()
                            history.push(URL_RESULTS.replace(":resultType", "succesfullyBooked"))
                            return
                        }
                        const scheduledPaymentId = scheduledPayment.id
                        const paymentReturnPath = URL_PAYMENT_RETURN.replace(":spId", scheduledPaymentId)
                        const currentLocale: string | undefined = i18n.language || undefined
                        writeMolliePendingEntry(bookingId, {
                            scheduledPaymentId,
                            molliePaymentId: null,
                            attemptKey: null,
                            createdAt: Date.now(),
                            redirectUrl: null,
                            bookingId,
                            method: effectiveMollieMethod,
                            locale: currentLocale,
                        })
                        try {
                            const mollieLocale = mapLocaleToMollie(i18n.language)
                            const mollieResult = await initiateMolliePayment({
                                variables: {
                                    bookingId,
                                    method: effectiveMollieMethod,
                                    scheduledPaymentId,
                                    locale: mollieLocale,
                                    returnBaseUrl: embedReturnBaseUrl(),
                                },
                            })
                            const mollieData = mollieResult.data?.initiateMolliePaymentForBookingFromEndUserApp
                            reportBookingConversion()
                            if (mollieData) {
                                writeMolliePendingEntry(bookingId, {
                                    scheduledPaymentId,
                                    molliePaymentId: mollieData.molliePaymentId,
                                    attemptKey: null,
                                    createdAt: Date.now(),
                                    redirectUrl: mollieData.checkoutUrl || null,
                                    bookingId,
                                    method: effectiveMollieMethod,
                                    locale: currentLocale,
                                })
                            }
                            if (effectiveMollieMethod === MOLLIE_BANKTRANSFER_METHOD) {
                                history.push(paymentReturnPath)
                                return
                            }
                            if (mollieData?.checkoutUrl) {
                                window.location.href = mollieData.checkoutUrl
                                return
                            }
                            // Buchung war erfolgreich, aber Mollie lieferte keine checkoutUrl —
                            // auf die Return-Page mit konkreter Rate leiten, damit Retry/Methodenwechsel
                            // an derselben ScheduledPayment-ID möglich bleibt.
                            history.push(paymentReturnPath)
                            return
                        } catch (mollieError) {
                            // Race-Fall (Kurs füllte sich zwischen createBooking-Antwort und
                            // Initiate): Der Server lehnt Zahlungen für Wartelistenplätze mit
                            // WAITLIST_PAYMENT_NOT_ALLOWED ab — dann ist die Buchung ein
                            // Wartelistenplatz und die Wartelisten-Erfolgsseite korrekt
                            // (kein Payment-Retry anbieten, der immer wieder scheitert).
                            if (getGraphQLErrorCode(mollieError) === "WAITLIST_PAYMENT_NOT_ALLOWED") {
                                history.push(URL_RESULTS.replace(":resultType", "successfullyOnWaitingList"))
                                return
                            }
                            // Buchung war erfolgreich, nur die Zahlungs-Initiierung schlug fehl —
                            // Return-Page behält die konkrete Rate für Retry/Methodenwechsel.
                            reportBookingConversion()
                            Sentry.captureException(mollieError)
                            history.push(paymentReturnPath)
                            return
                        }
                    }

                    // Inverse-Race auf Mollie-Pflicht-Kursen: Im Wartelisten-Modus wird keine
                    // Methode gewählt (waitlistPaymentDeferred) — wurde zwischenzeitlich ein
                    // Platz frei, ist die Buchung regulär und braucht eine Zahlung. Zur
                    // Return-Seite der ersten offenen Rate leiten (dort Methodenwahl + Zahlung),
                    // statt eine unbezahlte Pflicht-Buchung als Erfolg zu melden.
                    if (!effectiveMollieMethod && waitlistPaymentDeferred && !fullyCoveredByCredit && bookingType !== "Waitlist") {
                        const scheduledPayment = getFirstUnpaidScheduledPayment(booking.createBooking.scheduledPayments)
                        if (scheduledPayment && !(typeof scheduledPayment.paymentAmount === "number" && scheduledPayment.paymentAmount <= 0)) {
                            // bookingId als Query-Param: fuer Raten ohne bisherigen
                            // Zahlungsversuch kann die Return-Seite sonst keine
                            // initiate-Mutation starten (kein localStorage-Kontext,
                            // Status-Query liefert null) => 10s-Spinner + Sackgasse.
                            // waitlistPlaceFreed markiert GENAU diesen Inverse-Race: Der Kunde
                            // wollte auf die Warteliste und landet unerwartet im Zahlungsschritt.
                            // Die Return-Seite erklaert das mit einem Hinweis, statt den Sprung
                            // unkommentiert zu lassen.
                            history.push({
                                pathname: URL_PAYMENT_RETURN.replace(":spId", scheduledPayment.id),
                                search: `?bookingId=${encodeURIComponent(bookingId)}&waitlistPlaceFreed=1`,
                            })
                            return
                        }
                    }

                    // Standard-Flow: Erfolgsseite oder Warteliste
                    if (bookingType === "Waitlist") {
                        // Voraussetzung fehlt oder reiner Wartelisten-Kurs (0 Plätze): Die Ergebnisseite darf nicht „ausgebucht“ als Grund nennen.
                        const waitlistReason = prerequisiteWaitlistOnly ? "?reason=prerequisite" : course?.maxAttendees === 0 ? "?reason=waitlistOnly" : ""
                        history.push(URL_RESULTS.replace(":resultType", "successfullyOnWaitingList") + waitlistReason)
                    } else {
                        history.push(URL_RESULTS.replace(":resultType", "succesfullyBooked"))
                    }
                }
            }
        } catch (error: any) {
            // Late-Entry-Fehlerpfade zuerst über extensions.code matchen (verifiziertes
            // Backend-Format), VOR dem bestehenden String-Matching (bleibt als Fallback).
            const errorCode: string | undefined = error?.graphQLErrors?.[0]?.extensions?.code
            const discountRejection = getDiscountCodeRejection(error)
            if (discountRejection) {
                // Code wurde zwischen Vorschau und Absenden ungültig (abgelaufen, ausgeschöpft, deaktiviert):
                // im Checkout bleiben, Code entfernen, Preis neu rechnen – der Kunde bestätigt den normalen Preis.
                setDiscountCodeRejectedNotice(discountCodeRejectText(discountRejection.reason, t))
                rememberAppliedDiscount(null)
                updateBookingData("discountCode", "")
                setRejectedFingerprint(submittedFingerprint)
                forcePreviewRefresh()
                return
            }
            if (errorCode === "PRICE_QUOTE_CHANGED") {
                // Bewusst NICHT zum ResultScreen navigieren: der Kunde bleibt im Checkout.
                // Buchung gilt als nicht erfolgt. Den abgelehnten Fingerprint merken, damit
                // der Submit gesperrt bleibt, bis ein NEUER aus der Preview da ist (F2), und
                // einen UNGEDROSSELTEN Refresh auslösen (nicht am 15s-Throttle scheitern lassen).
                // Den Kurs mit neu laden: Zusatzposten, Preise oder freie Plätze können sich geändert haben.
                setRejectedFingerprint(submittedFingerprint)
                setPriceChangedNotice(true)
                setPriceUpdatedHint(false)
                forcePreviewRefresh()
                refreshCourseSnapshot()
                return
            }
            if (errorCode === "PAYMENT_METHOD_INCOMPLETE") {
                // Wie PRICE_QUOTE_CHANGED: im Checkout bleiben, kein ResultScreen, kein Sentry.
                // Die Lastschrift-Zeile bleibt geöffnet (kein Remount), der Hinweis steht in der
                // Bezahlmethoden-Sektion. Frischer Bucher, falls der Cache die Methode für
                // vollständig hielt.
                setRejectedPaymentMethodKey(selectedPaymentMethodKey)
                setShowAlert(true)
                refetchBooker({ id: booker?.id ?? "" }).catch(() => undefined)
                return
            }
            if (errorCode === "ATTENDEE_AGE_NOT_FITTING") {
                // Server prüft die Altersgrenze der Kursart (Backend ab 11/2026): im Checkout bleiben und
                // denselben Hinweis zeigen wie die Prüfung im Browser ("Teilnehmer passt nicht").
                setAgeRejectionKey(currentAgeKey)
                setShowAlert(true)
                return
            }
            if (errorCode === "NO_ELIGIBLE_LESSONS") {
                history.push(URL_RESULTS.replace(":resultType", "lateEntryNoEligibleLessons"))
                return
            }
            if (errorCode === "MINIMUM_PARTIAL_BOOKING_LESSON_COUNT_NOT_REACHED") {
                history.push(URL_RESULTS.replace(":resultType", "lateEntryMinimumNotReached"))
                return
            }
            if (errorCode === "LATE_ENTRY_CUSTOM_SCHEDULE_UNSUPPORTED" || errorCode === "ORIGINAL_PRICE_CUSTOM_SCHEDULE_TOTAL_MISMATCH") {
                history.push(URL_RESULTS.replace(":resultType", "lateEntryPricingUnavailable"))
                return
            }
            // Error wird unten via getGraphQLErrorMessage + Sentry verarbeitet —
            // hier kein extra console.log notwendig (Production noise).
            const rawErrorMessage = getGraphQLErrorMessage(error) || ""
            // console.log("rawErrorMessage", rawErrorMessage)

            const bookingRestrictionErrors = Object.entries(BookingRestrictionErrors).map(([key, value]) => {
                return {
                    key,
                    value,
                }
            })

            const bookingRestrictionErrorKeys = bookingRestrictionErrors.map((error) => {
                return error.key
            })

            // Typisierte Ablehnung (Backend #723): Code BOOKING_RESTRICTION mit dem Grund in
            // extensions.reason hat Vorrang; der String-Match bleibt als Fallback für
            // ältere Backend-Antworten (Contract erst nach sieben Prod-Tagen).
            const typedRestrictionReason: string | undefined =
                errorCode === "BOOKING_RESTRICTION" ? error?.graphQLErrors?.[0]?.extensions?.reason : undefined
            const foundBookingRestrictionErrorKey =
                (typedRestrictionReason && bookingRestrictionErrorKeys.includes(typedRestrictionReason) ? typedRestrictionReason : undefined) ??
                bookingRestrictionErrorKeys.find((key) => {
                    return rawErrorMessage.includes(key)
                })

            if (foundBookingRestrictionErrorKey) {
                // Fachliche Ablehnung, kein Absturz — bisher aber komplett telemetriefrei:
                // Der Kunde landete auf einer Ergebnisseite, in Sentry tauchte nichts auf.
                // Deshalb als Message (nicht als Exception) mit genau dem Kontext, der die
                // Ursache erklärt. PII-frei: nur IDs, Zähler und Flags.
                Sentry.captureMessage(`Buchung durch Buchungsbeschränkung abgelehnt: ${foundBookingRestrictionErrorKey}`, {
                    level: "warning",
                    tags: { bookingRestriction: foundBookingRestrictionErrorKey },
                    extra: {
                        courseId: course?.id,
                        selfBookerCourse: Boolean(selfBookerCourse),
                        selfBookerAttendeeChoiceRequired,
                        attendeeCount: booker?.attendees?.length ?? 0,
                        bookerOwnAttendeeExists: bookersOwnAttendee !== undefined,
                        bookerOwnAttendeeSelectable: bookersOwnAttendeeSelectable,
                        courseHasPrerequisites: isCourseWithPreRequisites,
                        membersOnly,
                    },
                })
                history.push(URL_RESULTS.replace(":resultType", foundBookingRestrictionErrorKey))
            } else if (rawErrorMessage.includes("Already enrolled")) {
                history.push(URL_RESULTS.replace(":resultType", "alreadyEnrolled"))
            } else if (rawErrorMessage.includes("Cannot read property 'courseId' of undefined")) {
                history.push(URL_RESULTS.replace(":resultType", "courseNotFound"))
            } else if (rawErrorMessage.includes("Access token not valid")) {
                history.push(URL_RESULTS.replace(":resultType", "loggedOut"))
            } else {
                Sentry.captureException(error)
                history.push(URL_RESULTS.replace(":resultType", "unknown"))
            }
        } finally {
            submittingRef.current = false
            setIsSubmitting(false)
        }
    }

    // Sackgassen-Schutz: Die Übersicht kennt den Kurs nur als Snapshot aus den Kursdetails. Wurde dort seither
    // ein gewählter Zusatzposten gelöscht (oder das Feature abgeschaltet), lehnt der Server jede Preview ab, und
    // weder Retry noch Reload laden den Kurs neu. Daher hier frisch laden; die Preview filtert die Auswahl gegen
    // den neuen Kurs und rechnet von selbst neu. Liefert der Server denselben Kurs, bleibt es beim Fehler mit Retry.
    const apolloClient = useApolloClient()
    const courseRefreshInFlightRef = useRef(false)
    // Stand DIESER Übersicht für den async-Callback. Der Storage ist zwischen Tabs geteilt: ist derselbe Kurs in
    // einem zweiten Tab offen, gehört bookingData dort womöglich zu dessen Auswahl.
    const shownCheckoutRef = useRef({ course, bookingData })
    shownCheckoutRef.current = { course, bookingData }
    const refreshCourseSnapshot = async () => {
        const courseId = course?.id
        if (!courseId || courseRefreshInFlightRef.current) return
        courseRefreshInFlightRef.current = true
        try {
            const { data } = await apolloClient.query<GetCourseDetailsByIdPublicQuery, GetCourseDetailsByIdPublicQueryVariables>({
                query: GetCourseDetailsByIdPublicDocument,
                variables: { courseId },
                fetchPolicy: "network-only",
            })
            const freshCourse = data?.courseByIdPublic
            // Liegt im Storage inzwischen ein anderer Kurs (Kursdetails in einem anderen Tab, Übersicht verlassen)
            // oder nichts mehr (Buchung abgeschlossen), nichts überschreiben.
            if (!freshCourse || safeJsonParse<Course>(safeStorage.getItem("course"))?.id !== courseId) return
            const { course: shownCourse, bookingData: shownBookingData } = shownCheckoutRef.current
            const selectedIds = shownBookingData?.additionalItemIds ?? []
            const keptIds = keepCourseAdditionalItemIds(selectedIds, freshCourse)
            const droppedTitles = (shownCourse?.additionalItems ?? []).filter((item) => selectedIds.includes(item.id) && !keptIds.includes(item.id)).map((item) => item.title)
            const freshCourseJson = JSON.stringify(freshCourse)
            safeStorage.setItem("course", freshCourseJson)
            setCourse(safeJsonParse<Course>(freshCourseJson) ?? undefined)
            if (keptIds.length !== selectedIds.length) updateBookingData("additionalItemIds", keptIds)
            if (droppedTitles.length > 0) setDroppedAdditionalItemTitles(droppedTitles)
        } catch {
            // Der Preview-Fehler mit Retry bleibt sichtbar; der nächste Fehlschlag lädt erneut.
        } finally {
            courseRefreshInFlightRef.current = false
        }
    }

    type KeyOfNewBookingInput = keyof NewBookingInput
    type ValueOf<T> = T[keyof T]
    type ValueOfNewBookingInput = ValueOf<NewBookingInput>

    const updateBookingData = (key: KeyOfNewBookingInput, value: ValueOfNewBookingInput) => {
        if (value !== undefined && value !== null) {
            // Funktional statt aus dem Render-Closure: eine späte Antwort der Preisvorschau (z. B. Code abgelehnt)
            // setzte sonst zwischenzeitlich gewählte Felder wie Zahlart, Notiz oder AGB-Haken zurück (C15, #1420).
            setBookingData((previous) => {
                const updatedBookingData = { ...previous }

                // @ts-ignore
                updatedBookingData[key] = value

                safeStorage.setItem("bookingData", JSON.stringify(updatedBookingData))

                return updatedBookingData
            })
        }
    }

    useEffect(() => {
        // THIS ISN'T A GOOD WAY TO DO THIS!
        const coursePriceCorrection = () => {
            let coursePrice = course?.coursePrice[0]
            const grossPrice = coursePrice?.grossPrice ?? 0
            const netPrice = coursePrice?.netPrice ?? grossPrice
            const lessonCount = course?.lessonCount ?? 0

            if (coursePrice && course?.paymentInterval === PaymentInterval.PerLesson) {
                coursePrice = {
                    ...coursePrice,
                    grossPrice: grossPrice * lessonCount,
                    netPrice: netPrice * lessonCount,
                }
            }

            return coursePrice
        }

        if (course && booker && attendeeId && attendee && course.coursePrice) {
            setBookingData((prevBookingData) => {
                const updatedBookingData: NewBookingInput = {
                    ...prevBookingData,
                    courseId: course.id,
                    attendeeId: attendeeId,
                    coursePriceId: course.coursePrice[0].id,
                    specialPrice: coursePriceCorrection()?.grossPrice,
                    vat: course.coursePrice[0].vat,
                    isSpecial: course.coursePrice[0].isSpecial,
                    netPrice: coursePriceCorrection()?.netPrice,
                    grossPrice: coursePriceCorrection()?.grossPrice,
                    startDateTime: course.startDateTime,
                    // Die Wahl des Kunden bleibt bei Teilnehmerwechsel, neu geladenem Bucher oder Kurs erhalten, solange
                    // der Kurs beide Zahlweisen anbietet; sonst gilt die des Kurses.
                    paymentType: resolvePaymentTypeChoice(prevBookingData?.paymentType, course),
                    monthlySchedule: course.monthlySchedule,
                }

                // Only update if there are changes
                if (JSON.stringify(prevBookingData) !== JSON.stringify(updatedBookingData)) {
                    safeStorage.setItem("bookingData", JSON.stringify(updatedBookingData))
                    return updatedBookingData
                }

                return prevBookingData
            })
        }
    }, [attendee, attendeeId, booker, course])

    const attendeesFirstName = booker?.attendees
        ?.filter((attendee) => {
            return attendee.id === bookingData?.attendeeId
        })
        .map((attendee) => attendee.firstname)[0]

    const bindingBookingText = media === "xs" || !attendeesFirstName ? "Buchung verbindlich abschließen" : `Buchung verbindlich für ${attendeesFirstName} abschließen`

    const waitlistText = media === "xs" ? "Wartelistenplatz buchen" : `Wartelistenplatz für ${attendeesFirstName} buchen`

    // Das Geburtsdatum des BUCHERS wird nur gebraucht, wenn aus ihm ein Teilnehmer
    // erzeugt wird. Wählt der Kunde einen vorhandenen Teilnehmer aus, zählt dessen
    // Geburtsdatum — die Abfrage hier würde den Checkout sonst grundlos blockieren.
    const selfBookerCourseWithoutBookersBirthday = selfBookerCourse && !booker?.birthday && !selfBookerAttendeeChoiceRequired
    // Phase 24 / D-04 — Primary-CTA-Text-Switch im Mollie-Modus.
    // Waitlist-Texte (mode === "PlaceReservation") bleiben unveraendert — Wartelisten-Buchungen
    // triggern keinen Mollie-Initiate (siehe submitBooking Z.176 `bookingType !== "Waitlist"`).
    const mollieModeText = t(
        "mollieCheckout.bookingSummary.primaryButton.mollieMode",
        "Verbindlich buchen und online bezahlen",
    )
    // Phase 4 — CTA-Text im Kombi-Modus: haengt an der gewaehlten Zahlart.
    // Mollie-Methode gewaehlt ⇒ Online-Wortlaut (combinedOnline); eigene Methode (oder
    // noch nichts) ⇒ neutraler Buchungs-Wortlaut (combinedOwn).
    const combinedOnlineText = t(
        "mollieCheckout.bookingSummary.primaryButton.combinedOnline",
        mollieModeText,
    )
    const combinedOwnText = t(
        "mollieCheckout.bookingSummary.primaryButton.combinedOwn",
        bindingBookingText,
    )
    const combinedModeText = selectedMollieMethod ? combinedOnlineText : combinedOwnText
    // Offener Betrag 0 durch Guthaben oder einen 100-%-Rabattcode: keine Mollie-Methode nötig, Mail sofort,
    // kein Mollie-Start (ohne Rate gäbe es keinen Webhook, der die Bestätigung nachholt).
    const fullyCoveredByCredit =
        ((bookingData?.creditedBalance ?? 0) > 0 || Boolean(bookingData?.discountCode)) && previewOpenAmount !== null && previewOpenAmount <= 0.005
    // Wartelistenplatz auf Mollie-Pflicht-Kurs: Die Methodenwahl entfaellt (SummaryRight
    // zeigt stattdessen den Wartelisten-Hinweis) — Zahlung erst beim Nachruecken.
    const waitlistPaymentDeferred = mode === "PlaceReservation" && isMollieMandatoryMode
    const submitButtonText =
        mode === "PlaceReservation"
            ? waitlistText
            : fullyCoveredByCredit
            ? bindingBookingText
            : isMollieMandatoryMode
            ? mollieModeText
            : isMollieCombinedMode
            ? combinedModeText
            : bindingBookingText
    // B-82808: Die Lastschrift-Sperre gilt nur, wenn die eigene Methode auch die wirksame Wahl
    // ist. Bei gewählter Online-Zahlung und im Mollie-Pflicht-Modus (kein eigener Picker sichtbar)
    // darf ein leerer Teilnehmer-Standard in `paymentMethodId` den Submit nicht blockieren.
    const ownPaymentMethodIsEffective = !selectedMollieMethod && !isMollieMandatoryMode
    const paymentMethodRejected = rejectedPaymentMethodKey !== null && rejectedPaymentMethodKey === selectedPaymentMethodKey
    const statusData: StatusData = {
        showAlert,
        noAttendeeDoesFitCourse,
        allAttendeesAlreadyBooked,
        bookerHasAttendees,
        bookingData,
        selfBookerCourse: selfBookerCourse ?? false,
        attendeeAgeDoesFitCourse: attendeeAgeDoesFitCourse && ageRejectionKey !== currentAgeKey,
        validSepaDebitDataValidIfSelected: !ownPaymentMethodIsEffective || (validSepaDebitDataValidIfSelected && !paymentMethodRejected),
        allReirementGroupsMet,
        optionalInputFields,
        selfBookerCourseWithoutBookersBirthday: selfBookerCourseWithoutBookersBirthday ?? false,
        creatingAttendee,
        inputFieldValues,
        selectedMollieMethod,
        fullyCoveredByCredit,
        waitlistPaymentDeferred,
        billingAddressIncomplete: billingAddressEnabled && isBillingAddressIncomplete(bookingData),
    }
    // Phase 24 / D-01 — Im Mollie-PFLICHT-Modus ist Submit disabled bis selectedMollieMethod
    // !== null (Online ist die einzige Zahlart; Kunde MUSS eine Mollie-Methode waehlen).
    //
    // Phase 4 — Im Kombi-Modus greift KEIN harter Mollie-Zwang: die bestehende
    // `isSubmitButtonDisabled`/`disabledText`-Logik akzeptiert `paymentMethodId` ODER
    // `selectedMollieMethod` (siehe disabledText.ts), sodass eine eigene Methode genauso
    // freigibt wie eine Online-Methode.
    // Late-Entry-Gate (Phase 3): rein additiv zum bestehenden Gating. Bewertet die zuletzt
    // gemeldete Preview-Quote (undefined = kein Flow, null = lädt, Objekt = fertig).
    const lateEntryGate = getLateEntryGate(quote, Date.now(), t, rejectedFingerprint)
    const submitButtonDisabled =
        isSubmitButtonDisabled(statusData) ||
        (isMollieMandatoryMode && !selectedMollieMethod && !fullyCoveredByCredit && !waitlistPaymentDeferred) ||
        lateEntryGate.blocked ||
        prerequisiteWaitlistFull
    const disabledEntries = getDisabledEntries(statusData, t)
    const disabledText = disabledEntries.map((entry) => entry.text)
    // Alle Gründe, warum der Submit gerade gesperrt wäre — für die Kundenakte.
    const blockerCodes: Array<CheckoutBlockerCode> = [
        ...disabledEntries.map((entry) => entry.code),
        ...(isMollieMandatoryMode && !selectedMollieMethod && !fullyCoveredByCredit && !waitlistPaymentDeferred ? (["MollieMethodMissing"] as Array<CheckoutBlockerCode>) : []),
        ...(lateEntryGate.blocked ? (["LateEntryGate"] as Array<CheckoutBlockerCode>) : []),
    ]
    latestReportRef.current = {
        ready: Boolean(course && booker && bookingData),
        courseId: course?.id ?? courseIdFromUrl ?? null,
        attendeeId: bookingData?.attendeeId ?? null,
        blockerCodes,
        hasPaymentMethod: Boolean(bookingData?.paymentMethodId || selectedMollieMethod || fullyCoveredByCredit || waitlistPaymentDeferred),
    }

    // Kundenakte: Zusammenfassung ohne createBooking verlassen (Navigation, Unmount,
    // Tab-Wechsel per pagehide). Nicht bei Mount-Guard-Redirects (ready=false) und
    // nicht nach gestartetem Submit. Dedupe je Kurs und Session liegt im Service.
    useEffect(() => {
        const reportAbandoned = () => {
            if (bookingStartedRef.current) return
            const latest = latestReportRef.current
            if (!latest.ready || !latest.courseId) return
            reportEndUserEvent({
                type: "CheckoutAbandoned",
                step: "BookingSummary",
                courseId: latest.courseId,
                attendeeId: latest.attendeeId,
                blockerCodes: latest.blockerCodes,
                hasPaymentMethod: latest.hasPaymentMethod,
            })
        }
        window.addEventListener("pagehide", reportAbandoned)
        return () => {
            window.removeEventListener("pagehide", reportAbandoned)
            reportAbandoned()
        }
    }, [])

    useEffect(() => {
        if (course) {
            const newMode: ModeType = course.freePlaces > 0 && !prerequisiteWaitlistOnly ? "createBooking" : "PlaceReservation"
            setMode(newMode)
        }
    }, [course, prerequisiteWaitlistOnly])

    useEffect(() => {
        const storedCourse = safeStorage.getItem("course")
        const storedAttendeeId = safeStorage.getItem("attendeeId")

        const timer = setTimeout(() => {
            const parsedCourse = safeJsonParse<Course>(storedCourse)
            if (parsedCourse) {
                if (!course || JSON.stringify(course) !== JSON.stringify(parsedCourse)) {
                    setCourse(parsedCourse)
                }
            }

            const parsedAttendeeId = safeJsonParse<string>(storedAttendeeId)
            if (parsedAttendeeId) {
                // Only set attendeeId if it's not already set
                setAttendeeId((prevAttendeeId) => prevAttendeeId || parsedAttendeeId)
            }
        }, 300)
        return () => clearTimeout(timer)
    }, [course])

    useEffect(() => {
        if (booker)
            booker.attendees?.forEach((attendee) => {
                if (attendee.id === attendeeId) {
                    setAttendee(attendee)
                }
            })
    }, [attendeeId, booker])

    useEffect(() => {
        if (isLoggedIn === false) {
            // Buchungskontext als fromUrl mitgeben, damit der User nach dem Login wieder
            // hier landet statt auf der Kurssuche (LoginPanel liest location.state.fromUrl).
            history.push(URL_LOGIN, { fromUrl: { pathname: location.pathname } })
        }
    }, [isLoggedIn, history, location.pathname])

    // Direktaufruf der Summary ohne Buchungskontext (fehlende oder korrupte Storage-Daten)
    // führte früher zu einem Crash bzw. einer Sackgasse. Stattdessen sauber zu den
    // Kursdetails (bzw. zur Kursauswahl) umleiten, damit die Buchung neu gestartet werden kann.
    useEffect(() => {
        if (!isLoggedIn) return // Login-Redirect (siehe oben) hat Vorrang
        const storedBookingData = safeJsonParse<NewBookingInput>(safeStorage.getItem("bookingData"))
        const storedCourse = safeJsonParse<Course>(safeStorage.getItem("course"))
        if (!storedBookingData || !storedCourse) {
            history.replace(courseIdFromUrl ? URL_COURSE_DETAILS.replace(":id", courseIdFromUrl) : URL_BASE)
            return
        }
        // Stale-Daten-Schutz: Der Storage wird nicht in jedem Fall geräumt (z.B. nach
        // paymentInitFailed). Ruft der User die Summary danach per URL direkt für einen
        // ANDEREN Kurs auf, dürfen die alten Daten (course.id bzw. bookingData.courseId
        // des vorherigen Kurses) nicht unter der neuen URL weiterverwendet werden — ein
        // Submit würde sonst den falschen Kurs buchen. Stattdessen zu den Kursdetails der
        // URL-courseId umleiten, damit die Buchung sauber neu startet (CourseDetails
        // schreibt course + bookingData dort passend neu in den Storage).
        // booker wird hier bewusst NICHT geprüft: er wird nach dem Mount per
        // GetUpdatedBooker-Query nachgeladen (siehe useGetUpdatedBookerQuery oben).
        if (courseIdFromUrl && (storedCourse.id !== courseIdFromUrl || storedBookingData.courseId !== courseIdFromUrl)) {
            history.replace(URL_COURSE_DETAILS.replace(":id", courseIdFromUrl))
        }
        // Bewusst nur beim Mount prüfen — danach hält die Komponente den State selbst aktuell.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <>
            <Card
                data-testid='booking-summary'
                bodyStyle={{ padding: 0, borderTop: `3px solid ${blue[1]}` }}
                title={
                    <h1 style={{ marginBottom: 0, paddingBottom: 0 }}>
                        <div className={css.titleSecondary}>
                            <div>Buchungsübersicht</div>
                            {media !== "xs" && (
                                <Tooltip
                                    trigger={["hover", "click" /* , "focus" */]}
                                    title={
                                        showAlert && disabledText.length
                                            ? disabledText.map((helperText, index) => {
                                                  return <div key={index}>{helperText}</div>
                                              })
                                            : null
                                    }
                                    placement='bottom'
                                    color={red[3]}
                                >
                                    <Button onClick={submitBooking} type='primary' loading={isSubmitting} disabled={submitButtonDisabled || isSubmitting || noAttendeeDoesFitCourse || !course || !booker || !bookingData} data-testid='booking-submit-button'>
                                        {`${submitButtonText}`}
                                    </Button>
                                </Tooltip>
                            )}
                        </div>
                    </h1>
                }
                actions={[
                    <div>
                        <Tooltip
                            trigger={["hover", "click", "focus"]}
                            title={
                                showAlert && disabledText.length
                                    ? disabledText.map((helperText, index) => {
                                          return <div key={index}>{helperText}</div>
                                      })
                                    : null
                            }
                            color={red[3]}
                        >
                            <Button
                                onClick={submitBooking}
                                block
                                type='primary'
                                loading={isSubmitting}
                                disabled={submitButtonDisabled || isSubmitting || noAttendeeDoesFitCourse || !course || !booker || !bookingData}
                                style={{ marginLeft: 10, marginRight: 10, maxWidth: "calc(100% - 20px)" }}
                            >
                                {`${submitButtonText}`}
                            </Button>
                            {media === "xs" && (
                                <Space style={{ fontWeight: 600, marginTop: 10 }} direction='vertical'>
                                    <div>{`Teilnehmer:in ${attendeesFirstName} ist ausgewählt`}</div>
                                </Space>
                            )}
                        </Tooltip>
                    </div>,
                ]}
            >
                {restrictions?.prerequisiteCourses && restrictions.prerequisiteCourses.length > 0 && (
                    <Row style={{ margin: 0 }}>
                        <PrerequisiteCourses
                            bookingRestriction={course?.bookingRestriction}
                            mediaColumns={mediaColumns}
                            alertMessage={
                                bookingRestriction?.allowWaitlistWithoutPrerequisite
                                    ? `Um diesen Kurs buchen zu können, muss der oder die Teilnehmerin mindestens in einen dieser Voraussetzungskurse eingebucht sein. ${t(
                                          "courseDetails.prerequisiteWaitlistHint",
                                          "Ohne den Voraussetzungskurs ist nur ein Eintrag auf die Warteliste möglich."
                                      )}`
                                    : "Um diesen Kurs buchen zu können, muss der oder die Teilnehmerin mindestens in einen dieser Voraussetzungskurse eingebucht sein."
                            }
                            direction='horizontal'
                        />
                    </Row>
                )}

                <Row style={{ margin: 0 }}>
                    <Col
                        span={media === "xs" ? 24 : 10}
                        style={{
                            background: "#eee",
                            padding: media === "sm" ? margin.xs : media === "xs" ? margin.s : margin.std,
                        }}
                    >
                        {/* Null-sichere Guards (== null): safeJsonParse liefert bei fehlendem oder
                            korruptem Storage-Inhalt null — die früheren `=== undefined`-Checks ließen
                            null durchrutschen. booker wird nach dem Mount per GetUpdatedBooker-Query
                            nachgeladen, bis dahin greift der Fallback unten. */}
                        {bookingData == null && <div>bookingData ist nicht da</div>}
                        {booker == null ? (
                            <Space
                                align='center'
                                direction='vertical'
                                style={{
                                    width: "100%",
                                }}
                            >
                                <h3>Teilnehmer fehlt</h3>
                            </Space>
                        ) : (
                            <>
                                {booker ? (
                                    <SummaryLeft
                                        booker={booker}
                                        loadingBooker={loadingBooker}
                                        attendee={attendee}
                                        bookingData={bookingData}
                                        updateBookingData={(key, value) => updateBookingData(key, value)}
                                        refetchBooker={() => refetchBooker({ id: booker?.id ?? "" })}
                                        setAttendeeAgeDoesFitCourse={(e: boolean) => setAttendeeAgeDoesFitCourse(e)}
                                        attendeeAgeDoesFitCourse={attendeeAgeDoesFitCourse}
                                        showAlert={showAlert}
                                        isMemberOnlyCourse={membersOnly}
                                        isCourseWithPreRequisites={isCourseWithPreRequisites}
                                        course={course}
                                        setNoAttendeeDoesFitCourse={(e: boolean) => setNoAttendeeDoesFitCourse(e)}
                                        setAllAttendeesAlreadyBooked={(e: boolean) => setAllAttendeesAlreadyBooked(e)}
                                        selfBookerCourse={selfBookerCourse ?? false}
                                        selfBookerAttendeeChoiceRequired={selfBookerAttendeeChoiceRequired}
                                        selectedAttendeeId={selectedAttendeeId}
                                        setSelectedAttendeeId={setSelectedAttendeeId}
                                    />
                                ) : (
                                    <Alert type='error' message='Fehler beim laden der Bucherdaten. Bitte versuchen Sie es später erneut.' />
                                )}
                            </>
                        )}
                    </Col>
                    <Col
                        span={media === "xs" ? 24 : 14}
                        style={{
                            background: "#fff",
                            padding: media === "sm" ? margin.xs : media === "xs" ? margin.s : margin.std,
                        }}
                    >
                        {/* Null-sicher (== null): booker und bookingData können durch safeJsonParse
                            null sein, course bis zum Storage-Sync undefined — beide Fälle abfangen. */}
                        {booker == null || course == null || bookingData == null ? (
                            <Space align='center' direction='vertical' style={{ width: "100%", display: "flex", justifyItems: "center" }}>
                                {booker == null && <div>Bucher fehlt</div>}
                                {course == null && <div>Kurs fehlt</div>}
                                {bookingData == null && <div>bookingData fehlt</div>}
                                <h3>Es wurde kein Kurs ausgewählt.</h3>
                                {/* F-002: history.push statt href='/' — mit HashRouter würde href='/'
                                    die App verlassen (führt auf die native Wurzel statt auf #/). */}
                                <Button type='primary' onClick={() => history.push(courseIdFromUrl ? URL_COURSE_DETAILS.replace(":id", courseIdFromUrl) : URL_BASE)}>
                                    Zurück zur Kursauswahl
                                </Button>
                            </Space>
                        ) : (
                            <>
                                {booker ? (
                                    <SummaryRight
                                        booker={booker}
                                        course={course}
                                        mode={mode}
                                        prerequisiteWaitlistOnly={prerequisiteWaitlistOnly}
                                        updateBookingData={(key, value) => updateBookingData(key, value)}
                                        bookingData={bookingData}
                                        liftSepaDebitData={(isValid: boolean) => setValidSepaDebitDataValidIfSelected(isValid)}
                                        paymentMethodIncomplete={ownPaymentMethodIsEffective && paymentMethodRejected}
                                        setCheckedOptions={setCheckedOptions}
                                        checkedOptions={checkedOptions}
                                        setRequermentGroupsMet={setRequermentGroupsMet}
                                        requermentGroupsMet={requermentGroupsMet}
                                        showAlert={showAlert}
                                        appSettings={appSettings}
                                        inputFieldValues={inputFieldValues}
                                        setInputFieldValues={setInputFieldValues}
                                        selectedMollieMethod={selectedMollieMethod}
                                        onMollieMethodSelect={handleMollieMethodSelect}
                                        fullyCoveredByCredit={fullyCoveredByCredit}
                                        onOpenAmountChange={setPreviewOpenAmount}
                                        onQuoteChange={setQuote}
                                        refreshNonce={refreshNonce}
                                        onCourseOutdated={refreshCourseSnapshot}
                                    />
                                ) : (
                                    <Alert type='error' message='Fehler beim laden der Bucherdaten. Bitte versuchen Sie es später erneut.' />
                                )}
                            </>
                        )}
                    </Col>
                </Row>
                {discountCodeRejectedNotice && (
                    <Row ref={discountCodeRejectedNoticeRef} style={{ margin: 0, padding: `0 ${margin.std}px` }}>
                        <Alert
                            style={{ width: "100%", marginTop: margin.s }}
                            type='warning'
                            showIcon
                            closable
                            onClose={() => setDiscountCodeRejectedNotice(null)}
                            data-testid='discount-code-rejected-notice'
                            message={t("discountCode.rejectedOnSubmit", {
                                defaultValue: "Der Rabattcode konnte nicht mehr eingelöst werden: {{reason}} Es gilt der normale Preis – bitte prüfe ihn und bestätige erneut.",
                                reason: discountCodeRejectedNotice,
                            })}
                        />
                    </Row>
                )}
                {droppedAdditionalItemTitles.length > 0 && (
                    <Row ref={droppedAdditionalItemsNoticeRef} style={{ margin: 0, padding: `0 ${margin.std}px` }}>
                        <Alert
                            style={{ width: "100%", marginTop: margin.s }}
                            type='warning'
                            showIcon
                            closable
                            onClose={() => setDroppedAdditionalItemTitles([])}
                            data-testid='additional-items-dropped-notice'
                            message={
                                <>
                                    {droppedAdditionalItemTitles.map((title, index) => (
                                        <div key={index}>
                                            {t("additionalItems.noLongerOffered", {
                                                defaultValue: "„{{title}}“ wird für diesen Kurs nicht mehr angeboten und wurde aus deiner Auswahl entfernt.",
                                                title,
                                            })}
                                        </div>
                                    ))}
                                    <div>{t("additionalItems.checkPrice", "Bitte prüfe den Preis, bevor du die Buchung abschließt.")}</div>
                                </>
                            }
                        />
                    </Row>
                )}
                {priceChangedNotice && (
                    <Row style={{ margin: 0, padding: `0 ${margin.std}px` }}>
                        <Alert
                            style={{ width: "100%", marginTop: margin.s }}
                            type='warning'
                            showIcon
                            data-testid='price-quote-changed-notice'
                            message={t(
                                "lateEntry.priceQuoteChanged",
                                "Der Preis oder Zahlungsplan hat sich geändert – bitte prüfe den aktualisierten Preis und bestätige erneut.",
                            )}
                        />
                    </Row>
                )}
                {!priceChangedNotice && priceUpdatedHint && (
                    <Row style={{ margin: 0, padding: `0 ${margin.std}px` }}>
                        <Alert
                            style={{ width: "100%", marginTop: margin.s }}
                            type='info'
                            showIcon
                            data-testid='price-updated-notice'
                            message={t("lateEntry.priceUpdated", "Der Preis wurde aktualisiert.")}
                        />
                    </Row>
                )}
                {lateEntryGate.blocked && lateEntryGate.reason && (
                    <Row style={{ margin: 0, padding: `0 ${margin.std}px` }}>
                        <Alert
                            style={{ width: "100%", marginTop: margin.s }}
                            type={lateEntryGate.retry ? "error" : lateEntryGate.expired || quote === null ? "info" : "warning"}
                            showIcon
                            data-testid='late-entry-gate-reason'
                            message={lateEntryGate.reason}
                            action={
                                lateEntryGate.retry ? (
                                    <Button size='small' data-testid='late-entry-retry' onClick={forcePreviewRefresh}>
                                        {t("lateEntry.retry", "Erneut versuchen")}
                                    </Button>
                                ) : undefined
                            }
                        />
                    </Row>
                )}
                {showAlert && <RequiredAlert disabledText={() => disabledText} small />}
            </Card>
        </>
    )
}

export default memo(BookingSummary)
